import { memo, useCallback } from 'react';
import { HandleOpenNewLegModal, LegConnectorConnectionType } from '@lp/features/RoutePlanner/types';
import { useRouteContext } from '@lp/core/contexts/RouteContext';
import { GroundTripLegWarnings } from '@lp/features/RoutePlanner/components/RouteViewList/GroundTripLegWarnings';
import { formatDateTimeStringToTimeZone } from '@/utils/helpers';
import { SegmentTypeEnum, AviaLegTypes, GroundLegTypes, RouteTypeEnum } from '@/api/apis/route';
import { useBaseCaseDetails } from '@/hooks/useBaseCaseDetails';
import { isGroundTripLegType } from '@/utils/route';
import { RouteSegmentType } from '@/types/route';
import { LegConnector } from './LegConnector';
import { SegmentLegInformation } from './SegmentLegInformation';
import { SegmentLegWarnings } from './SegmentLegWarnings';

type SegmentLegProps = {
  type?: 'logistics' | 'suggestion';
  routeType: RouteTypeEnum;
  leg: AviaLegTypes | GroundLegTypes;
  prevLeg?: AviaLegTypes | GroundLegTypes;
  segment: RouteSegmentType;
  isReadOnly: boolean;
  isDisabled: boolean;
  showAddLeg?: boolean;
  disableHintText?: string;
  handleAddNewLeg?: (data: Partial<HandleOpenNewLegModal>) => void;
  handleLegInfoClick?: (leg: AviaLegTypes | GroundLegTypes, segmentId: string | null, prevLegId: string | null) => void;
};

export const SegmentLeg = memo(
  ({
    type = 'logistics',
    routeType,
    leg,
    prevLeg,
    segment,
    showAddLeg = false,
    isReadOnly,
    isDisabled,
    disableHintText,
    handleAddNewLeg,
    handleLegInfoClick,
  }: SegmentLegProps) => {
    const { locationById } = useRouteContext();
    const { isLightsSirensRequired } = useBaseCaseDetails();

    const isStartLocation = leg.departureLocation.locationId === segment.startLocation.locationId;
    const isEndLocation = leg.arrivalLocation.locationId === segment.endLocation.locationId;

    const showBottomConnector =
      ![SegmentTypeEnum.reposition, SegmentTypeEnum.returnToBase, SegmentTypeEnum.custom].includes(leg.segmentType) &&
      isEndLocation;

    const startLocation = locationById.get(leg.departureLocation.locationId);
    const endLocation = locationById.get(leg.arrivalLocation.locationId);
    const startLocationTimeZone =
      startLocation?.location.timezone || endLocation?.location.timezone || 'America/New_York';
    const endLocationTimeZone =
      endLocation?.location.timezone || startLocation?.location.timezone || 'America/New_York';

    const startTime = formatDateTimeStringToTimeZone(leg.departureTime, startLocationTimeZone, 'MMM dd, HH:mm');
    const endTime = formatDateTimeStringToTimeZone(leg.arrivalTime, endLocationTimeZone, 'MMM dd, HH:mm');

    const isLogisticsView = type === 'logistics';
    const isGroundTripLegs = isGroundTripLegType(leg) && isLogisticsView;

    const onAddLegClick = useCallback(
      () => handleAddNewLeg?.({ segmentId: segment.id, prevLegId: leg.id }),
      [handleAddNewLeg, segment.id, leg.id],
    );

    return (
      <div className="flex gap-4 items-stretch">
        <div className="flex gap-1 w-33 -translate-y-3 justify-end shrink-0">
          {isGroundTripLegs ? (
            <GroundTripLegWarnings leg={leg} prevLeg={prevLeg} segment={segment} />
          ) : (
            <SegmentLegWarnings leg={leg} prevLeg={prevLeg} segment={segment} />
          )}
          <div className="flex flex-col items-end">
            <span className="text-s-r text-base-10">{startTime}</span>
            <span className="text-s-r text-base-10">{endTime}</span>
          </div>
        </div>
        <LegConnector
          type={leg.legType}
          startConnection={
            isStartLocation ? LegConnectorConnectionType.startToEnd : LegConnectorConnectionType.inbetween
          }
          endConnection={isEndLocation ? LegConnectorConnectionType.startToEnd : LegConnectorConnectionType.inbetween}
          segmentType={leg.segmentType}
          showAddLegButton={!isReadOnly && showAddLeg}
          isDisableAddLegButton={isDisabled}
          disableHintText={disableHintText}
          showBottomConnector={showBottomConnector}
          onAddLegClick={onAddLegClick}
        />
        <SegmentLegInformation
          type={type}
          routeType={routeType}
          leg={leg}
          segment={segment}
          handleLegInfoClick={handleLegInfoClick}
          isLightsSirensRequired={isLightsSirensRequired}
        />
      </div>
    );
  },
);
